import React, { useEffect, useMemo, useState } from 'react';
import { Download, FileText, Printer, RefreshCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { systemService } from '../../../services/systemService';
import { getStudentFeeStatementSummary, getStudentFeeStructure } from '../../../services/feeService';
import { getActiveEnrollmentContext } from '../../../services/enrollmentService';
import { createDocumentQrValue, downloadHtmlAsPdf, formatServerDateTime, getServerTimestamp } from '../../../utils/documentPdf';

const emptyFeeData = {
  m1: 0,
  m2: 0,
  m3: 0,
  billingMode: 'program_based',
  billingModeLabel: 'Programme-based tuition',
  summativeExamBookings: [],
  institution: null,
};

const numeric = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = value => numeric(value).toLocaleString('en-KE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const dateLabel = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('en-KE', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(date);
};

const invoiceReference = (student, generatedAt) => {
  const identifier = String(student?.admission_number || student?.registration_number || 'STUDENT')
    .replace(/[^a-z0-9]/gi, '')
    .toUpperCase()
    .slice(-18) || 'STUDENT';
  const issueDate = generatedAt ? new Date(generatedAt).toISOString().slice(0, 10).replaceAll('-', '') : 'PENDING';
  return `PFI-${identifier}-${issueDate}`;
};

const ProformaInvoice = ({ student }) => {
  const [institution, setInstitution] = useState(null);
  const [feeData, setFeeData] = useState(emptyFeeData);
  const [statement, setStatement] = useState(null);
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    getServerTimestamp().then(setGeneratedAt).catch(() => {});
  }, []);

  useEffect(() => {
    let current = true;
    const load = async () => {
      const studentId = student?.profile_id || student?.id;
      if (!studentId) {
        if (current) setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const [institutionResult, feeResult, statementResult, enrollmentResult] = await Promise.all([
          systemService.getInstitutionDetails(),
          getStudentFeeStructure(student),
          getStudentFeeStatementSummary(student),
          getActiveEnrollmentContext(),
        ]);
        if (!current) return;
        setInstitution(feeResult?.institution || institutionResult || null);
        setFeeData({ ...emptyFeeData, ...(feeResult || {}) });
        setStatement(statementResult || null);
        setPeriod(enrollmentResult?.success ? enrollmentResult.data : null);
      } catch (loadError) {
        if (!current) return;
        console.error('Unable to load proforma invoice:', loadError);
        setError(loadError?.message || 'The proforma invoice could not be prepared.');
      } finally {
        if (current) setLoading(false);
      }
    };

    load();
    return () => { current = false; };
  }, [student?.profile_id, student?.id, student?.program_id, student?.program?.id, student?.academic_level]);

  const moduleNumber = Math.max(numeric(student?.current_module || student?.starting_module || 1), 1);
  const moduleColumn = [1, 2, 3].includes(moduleNumber) ? moduleNumber : ((moduleNumber - 1) % 3) + 1;
  const moduleLabel = `Module ${moduleNumber}`;
  const statutoryFees = institution?.statutory_fees_config || feeData.institution?.statutory_fees_config || [];

  const lineItems = useMemo(() => {
    const rows = [];
    const tuition = numeric(feeData[`m${moduleColumn}`]);
    if (tuition > 0) {
      rows.push({
        key: 'tuition',
        category: 'Tuition',
        description: `${moduleLabel} tuition fees · ${feeData.billingModeLabel || 'Programme-based tuition'}`,
        amount: tuition,
      });
    }

    statutoryFees.forEach((fee, index) => {
      if ((fee.modules || []).map(Number).includes(moduleColumn) && numeric(fee.amount) > 0) {
        rows.push({
          key: `statutory-${index}`,
          category: 'Institutional fee',
          description: fee.name || 'Institutional fee',
          amount: numeric(fee.amount),
        });
      }
    });

    (feeData.summativeExamBookings || [])
      .filter(booking => Number(booking.module_number || 1) === moduleNumber)
      .forEach((booking, index) => {
        const unitCode = booking.unit?.code || booking.unit_id || 'Registered unit';
        rows.push({
          key: `exam-${booking.id || index}`,
          category: 'Assessment',
          description: `Summative examination · ${unitCode}${booking.unit?.name ? ` · ${booking.unit.name}` : ''}`,
          amount: numeric(booking.fee_amount),
        });
      });

    return rows;
  }, [feeData, moduleColumn, moduleLabel, moduleNumber, statutoryFees]);

  const subtotal = lineItems.reduce((total, item) => total + item.amount, 0);
  const statementBalance = numeric(statement?.balance);
  const availableCredit = Math.max(-statementBalance, 0);
  const creditApplied = Math.min(availableCredit, subtotal);
  const estimatedPayable = Math.max(subtotal - creditApplied, 0);
  const remainingCredit = Math.max(availableCredit - subtotal, 0);
  const academicYear = period?.academicYear?.name || period?.academicYear?.year_name || 'Current academic year';
  const semester = period?.semester?.name || period?.semester?.semester_name || period?.semester?.semester_type || 'Current semester';
  const periodDates = period?.semester?.start_date
    ? `${dateLabel(period.semester.start_date)}${period.semester.end_date ? ` – ${dateLabel(period.semester.end_date)}` : ''}`
    : '';
  const studentName = [student?.first_name, student?.middle_name, student?.last_name].filter(Boolean).join(' ') || 'Student';
  const invoiceNo = invoiceReference(student, generatedAt);

  const downloadPdf = async () => {
    const element = document.getElementById('student-proforma-invoice');
    if (!element) return;
    try {
      const serverTimestamp = await getServerTimestamp();
      setGeneratedAt(serverTimestamp);
      await downloadHtmlAsPdf({ element, filename: `${invoiceReference(student, serverTimestamp)}.pdf`, title: 'Student Proforma Invoice' });
    } catch (downloadError) {
      console.error('Failed to generate proforma invoice PDF:', downloadError);
      setError(downloadError?.message || 'The PDF could not be generated.');
    }
  };

  if (loading) {
    return <div className="grid min-h-64 place-items-center rounded-xl border border-slate-200 bg-white"><RefreshCw className="h-7 w-7 animate-spin text-primary" /></div>;
  }

  if (error) {
    return <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">{error}</div>;
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Finance</p>
          <h1 className="text-2xl font-bold text-slate-950">Student proforma invoice</h1>
          <p className="mt-1 text-sm text-slate-500">Estimated charges for the current academic period.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"><Printer className="h-4 w-4" />Print</button>
          <button type="button" onClick={downloadPdf} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"><Download className="h-4 w-4" />Download PDF</button>
        </div>
      </div>

      <article id="student-proforma-invoice" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm print:rounded-none print:border-0 print:shadow-none">
        <div className="border-b-4 border-primary px-6 py-7 sm:px-10">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex items-center gap-4">
              {institution?.logo_url ? <img src={institution.logo_url} alt="Institution logo" className="h-16 w-16 rounded-xl border border-slate-200 bg-white object-contain p-1" /> : <div className="grid h-16 w-16 place-items-center rounded-xl bg-primary/10 text-primary"><FileText className="h-8 w-8" /></div>}
              <div>
                <p className="text-lg font-bold uppercase tracking-wide text-slate-950">{institution?.name || 'Institution'}</p>
                {institution?.motto && <p className="mt-0.5 text-xs italic text-slate-500">{institution.motto}</p>}
                <p className="mt-1 text-xs text-slate-500">{institution?.postal_address || institution?.address || institution?.email || ''}</p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Proforma invoice</p>
              <p className="mt-2 font-mono text-sm font-semibold text-slate-900">{invoiceNo}</p>
              <p className="mt-1 text-xs text-slate-500">Issued {formatServerDateTime(generatedAt)}</p>
              <span className="mt-3 inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold text-amber-800">Estimate · Not a receipt</span>
            </div>
          </div>
        </div>

        <div className="px-6 py-7 sm:px-10">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Bill to</p>
              <p className="mt-2 text-base font-bold text-slate-950">{studentName}</p>
              <p className="mt-1 font-mono text-xs text-slate-600">{student?.admission_number || student?.registration_number || 'Admission number not set'}</p>
              <p className="mt-1 text-xs text-slate-600">{student?.institutional_email || student?.email || '—'}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Academic period</p>
              <p className="mt-2 text-base font-bold text-slate-950">{academicYear}</p>
              <p className="mt-1 text-sm text-slate-700">{semester} · {moduleLabel}</p>
              <p className="mt-1 text-xs text-slate-600">{student?.program?.name || 'Programme not set'} · {student?.academic_level || 'Level not set'}</p>
              {periodDates && <p className="mt-1 text-xs text-slate-500">{periodDates}</p>}
            </div>
          </div>

          <div className="mt-8 overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-slate-900 text-left text-xs uppercase tracking-wide text-white">
                <tr><th className="px-4 py-3">Category</th><th className="px-4 py-3">Description</th><th className="px-4 py-3 text-right">Amount (KES)</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {lineItems.length ? lineItems.map(item => <tr key={item.key}><td className="px-4 py-3 font-semibold text-slate-700">{item.category}</td><td className="px-4 py-3 text-slate-600">{item.description}</td><td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(item.amount)}</td></tr>) : <tr><td colSpan="3" className="px-4 py-10 text-center text-slate-500">No charges are configured for this period.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex justify-end">
            <div className="w-full max-w-sm space-y-2 text-sm">
              <div className="flex justify-between gap-4 text-slate-600"><span>Subtotal</span><span className="font-semibold text-slate-900">KES {formatMoney(subtotal)}</span></div>
              {creditApplied > 0 && <div className="flex justify-between gap-4 text-emerald-700"><span>Statement credit applied</span><span className="font-semibold">− KES {formatMoney(creditApplied)}</span></div>}
              <div className="flex justify-between gap-4 border-t-2 border-slate-900 pt-3 text-base font-bold text-slate-950"><span>Estimated amount payable</span><span>KES {formatMoney(estimatedPayable)}</span></div>
            </div>
          </div>

          {availableCredit > 0 && <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"><p className="font-bold">Credit balance available</p><p className="mt-1">Your fee statement has a credit of KES {formatMoney(availableCredit)}. {remainingCredit > 0 ? `KES ${formatMoney(remainingCredit)} remains after this proforma invoice.` : 'It has been applied against this estimate.'}</p></div>}
          {statementBalance > 0 && <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"><p className="font-bold">Existing statement balance</p><p className="mt-1">Your current fee statement shows KES {formatMoney(statementBalance)} outstanding. This estimate covers the charges listed above.</p></div>}

          <div className="mt-8 border-t border-slate-200 pt-4 text-xs leading-relaxed text-slate-500">
            <p>This proforma invoice is an estimate for planning and registration. It is not a receipt and does not confirm payment. Final charges are subject to approved institutional fees and Finance posting.</p>
            <div className="mt-4 flex flex-wrap justify-between gap-3"><span>Billing basis: {feeData.billingModeLabel || 'Programme-based tuition'}</span><span>Payment due: On registration</span></div>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4 border-t border-slate-200 pt-4">
              <span>Generated by: System Generated</span>
              <div className="flex items-center gap-3"><QRCodeSVG value={createDocumentQrValue({ type: 'proforma_invoice', documentId: invoiceNo, student, generatedAt, extra: { document: 'Student Proforma Invoice', subtotal, estimated_payable: estimatedPayable, academic_year: academicYear, semester } })} size={96} level="M" /><span>{formatServerDateTime(generatedAt)}</span></div>
            </div>
          </div>
        </div>
      </article>

      <style jsx="true">{`
        @media print {
          body * { visibility: hidden; }
          #root, #root * { visibility: visible; }
          header, nav, aside, footer { display: none !important; }
          .print\\:hidden { display: none !important; }
          #student-proforma-invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
          }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
};

export default ProformaInvoice;
